/**
 * Consultant Dashboard Page
 *
 * Main landing page for consultants showing:
 * - Overview statistics (active clients, pending pre-audits, completed audits)
 * - Assigned clients list
 * - Recent activity
 * - Upcoming deadlines
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, ClipboardList, CheckCircle2, FileText, Check, BarChart3 } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { StatCard } from '../../components/dashboard/StatCard';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { RecentActivityCard } from '../../components/dashboard/RecentActivityCard';
import { QuickActionsPanel } from '../../components/dashboard/QuickActionsPanel';
import type { ActivityItem } from '../../components/dashboard/RecentActivityCard';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

export const ConsultantDashboardPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant']);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const appsResponse = await mockApi.getAllApplications();
      if (appsResponse.success && appsResponse.data) {
        setApplications(appsResponse.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = {
    activeClients: applications.filter(a =>
      ['SUBMITTED', 'UNDER_REVIEW', 'CONSULTANT_ASSIGNED', 'PRE_AUDIT_SCHEDULED'].includes(a.status)
    ).length,
    pendingPreAudits: applications.filter(a =>
      a.status === 'CONSULTANT_ASSIGNED' || a.status === 'PRE_AUDIT_SCHEDULED'
    ).length,
    completedThisMonth: applications.filter(a => {
      const completedDate = new Date(a.updatedAt);
      const now = new Date();
      return a.status === 'PRE_AUDIT_COMPLETED' &&
             completedDate.getMonth() === now.getMonth() &&
             completedDate.getFullYear() === now.getFullYear();
    }).length,
  };

  // Get my clients (applications assigned to consultant)
  const myClients = applications.filter(a =>
    ['CONSULTANT_ASSIGNED', 'PRE_AUDIT_SCHEDULED', 'PRE_AUDIT_COMPLETED'].includes(a.status)
  ).slice(0, 5);

  // Get recent activity (latest updated applications)
  const recentActivity = [...applications]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'PRE_AUDIT_COMPLETED':
        return 'success';
      case 'CONSULTANT_ASSIGNED':
      case 'PRE_AUDIT_SCHEDULED':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'UNDER_REVIEW':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('consultant:dashboard.title')}
        footer={{ copyright: "© 2026 HALMAS. Araken Sdn. Bhd. All rights reserved." }}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:dashboard.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  // Convert activity data to new format
  const activityItems: ActivityItem[] = recentActivity.map((application) => ({
    id: application.id,
    icon: <FileText className="w-4 h-4" />,
    title: application.companyName,
    description: `${application.status.replace(/_/g, ' ')}`,
    timestamp: new Date(application.updatedAt),
    variant: getStatusVariant(application.status) === 'warning' ? 'warning' : 'default',
    onClick: () => window.location.href = `/consultant/clients/${application.id}`,
  }));

  return (
    <PageLayout
      pageTitle={t('consultant:dashboard.title')}
      footer={{ copyright: "© 2026 HALMAS. Araken Sdn. Bhd. All rights reserved." }}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:dashboard.title') },
      ]}
    >
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('consultant:dashboard.welcome')}</h2>
        <p className="text-gray-600">{t('consultant:dashboard.overview')}</p>
      </div>

      {/* Statistics Cards */}
      <DashboardGrid cols={3} className="mb-8">
        <StatCard
          title={t('consultant:dashboard.assignedClients')}
          value={stats.activeClients}
          icon={<Users className="w-6 h-6" />}
          variant="primary"
          subtitle="Active client engagements"
          action={{
            label: t('consultant:dashboard.viewAllClients'),
            onClick: () => window.location.href = '/consultant/clients',
          }}
        />

        <StatCard
          title={t('consultant:dashboard.pendingPreAudits')}
          value={stats.pendingPreAudits}
          icon={<ClipboardList className="w-6 h-6" />}
          variant={stats.pendingPreAudits > 0 ? 'warning' : 'default'}
          subtitle="Awaiting pre-audit completion"
          action={{
            label: 'View Pre-Audits',
            onClick: () => window.location.href = '/consultant/pre-audit',
          }}
        />

        <StatCard
          title={t('consultant:dashboard.completedThisMonth')}
          value={stats.completedThisMonth}
          icon={<CheckCircle2 className="w-6 h-6" />}
          variant="success"
          trend={{
            value: 15,
            direction: 'up',
            label: 'vs last month',
          }}
          subtitle="Pre-audits completed"
        />
      </DashboardGrid>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Clients */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('consultant:dashboard.myClients')}</h3>
              <Button variant="outline" onClick={() => window.location.href = '/consultant/clients'}>
                {t('consultant:dashboard.viewAllClients')}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {myClients.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{t('consultant:clients.noClients')}</p>
            ) : (
              <div className="space-y-4">
                {myClients.map((application) => (
                  <div
                    key={application.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.location.href = `/consultant/clients/${application.id}`}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{application.companyName}</h4>
                      <p className="text-sm text-gray-500">
                        {application.type} - {formatDate(application.submittedAt || application.createdAt)}
                      </p>
                    </div>
                    <Badge variant={getStatusVariant(application.status)}>
                      {application.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Recent Activity */}
        <RecentActivityCard
          title={t('consultant:dashboard.recentActivity')}
          items={activityItems}
          maxItems={5}
          showViewAll
          onViewAllClick={() => window.location.href = '/consultant/clients'}
        />
      </div>

      {/* Quick Actions */}
      <QuickActionsPanel
        title="Quick Actions"
        layout="grid"
        className="mt-6"
        actions={[
          {
            id: 'clients',
            label: t('consultant:clients.title'),
            icon: <Users className="w-4 h-4" />,
            onClick: () => window.location.href = '/consultant/clients',
            variant: 'outline',
          },
          {
            id: 'pre-audit',
            label: t('consultant:preAudit.title'),
            icon: <Check className="w-4 h-4" />,
            onClick: () => window.location.href = '/consultant/pre-audit',
            variant: 'outline',
          },
          {
            id: 'reports',
            label: t('consultant:reports.title'),
            icon: <BarChart3 className="w-4 h-4" />,
            onClick: () => window.location.href = '/consultant/reports',
            variant: 'outline',
          },
          {
            id: 'car',
            label: t('consultant:car.title'),
            icon: <FileText className="w-4 h-4" />,
            onClick: () => window.location.href = '/consultant/car',
            variant: 'outline',
          },
        ]}
      />
    </PageLayout>
  );
};

export default ConsultantDashboardPage;
